/**
 * Session asset-fidelity policy. Full desktop quality renders the graduated art
 * the owner reviews; mobile/explicit-low keeps the lightweight procedural LOD0
 * path. Capture mode may override either direction for controlled A/B evidence.
 *
 * `q-flora-low-tier-textures`: a pack may opt its textured base into the LOW
 * profile on named render tiers (`AssetPackDef.lowQualityBase`, content data), so
 * the low tier's flora can carry the painted texture while every other pack keeps
 * LOD0. Removing a tier from that list turns it off there with no code change.
 */

import type { AssetRenderTier } from '@great-cloud/shared/client';

import { capturedTexturesPreference } from '../capture.js';
import type { RenderTier } from './budget.js';
import { activeQualityProfile } from './quality.js';

export function resolveFullDetailAssets(
  qualityLow: boolean,
  capturePreference: boolean | null,
): boolean {
  return capturePreference ?? !qualityLow;
}

/** Resolve the current browser session's asset-detail choice. */
export function fullDetailAssetsActive(): boolean {
  return resolveFullDetailAssets(activeQualityProfile().low, capturedTexturesPreference());
}

/** A pack's low-profile opt-in, as the client content pack ships it. */
export interface LowQualityBaseOptIn {
  readonly lowQualityBase?: readonly AssetRenderTier[];
}

/**
 * Whether THIS pack draws its textured base. Full quality always does (as
 * `resolveFullDetailAssets`); the low profile does only when the pack lists the
 * session's render tier in `lowQualityBase`; an absent list, or no pack at all,
 * keeps LOD0 exactly as before the field existed. Capture mode still wins both ways.
 */
export function resolvePackBaseAssets(
  profile: { readonly low: boolean; readonly tier: RenderTier },
  pack: LowQualityBaseOptIn | null | undefined,
  capturePreference: boolean | null,
): boolean {
  if (capturePreference !== null) return capturePreference;
  if (!profile.low) return true;
  return pack?.lowQualityBase?.includes(profile.tier) ?? false;
}

/** Resolve `resolvePackBaseAssets` for the current browser session. */
export function packBaseAssetsActive(pack: LowQualityBaseOptIn | null | undefined): boolean {
  return resolvePackBaseAssets(activeQualityProfile(), pack, capturedTexturesPreference());
}
